import type {PhoneMask} from '@yandex-delivery/core/formatters'
import type {PhoneRule} from '@yandex-delivery/core/phones/types'
import type {DeviceOS, DeviceVendor} from '@yandex-delivery/express/middleware'
import type {Brand} from '@yandex-delivery/express/utils'
import type {Device} from '@yandex-delivery/modules/asset-manager/types'
import type {V2FormRequest} from '@yandex-delivery/types/schemas/services/delivery-corp-account-api/docs/yaml/definitions/delivery_form'

import type {fetchAddressSuggest} from '../api'
import type {LaunchResponse} from '../api/cargo-corp'
import type {UtmParameters} from '../api/cargo-crm'
import type {MakeUrlApi} from './make-url-api'

export type Point = [number, number]

export type Dir = 'ltr' | 'rtl'

export type InitialRequest = Omit<V2FormRequest, 'mode'>

export const enum CarBodyType {
  Van = 'van',
  LCV_M = 'lcv_m',
  LCV_L = 'lcv_l',
  LCV_XL = 'lcv_xl',
  LCV_XXL = 'truck_5t',
}

export type AvailableTariff =
  | 'express'
  | 'cargo'
  | 'ndd_self_pickup'
  | 'ndd_shipment'
  | 'courier'
  | 'walking_courier'
  | 'cargo_intercity'

export type InitialProps = {
  availableTariffs: AvailableTariff[]
  zonePoint: Point
  bduiInitialRequest: InitialRequest
  zoneName?: string
  from?: Point
  tariff?: string
  to?: Point
  requirement?: string
}

export type RoutesConfig = {
  home: string
}

export type ParcelSize = {
  id: string
  height: number
  length: number
  weight: number
  width: number
}

export type CreativeBlockParams = {
  goal?: string
  link?: string
  desktopImageSrc?: string
  mobileImageSrc?: string
  targetBlank?: boolean
}

export type Experiments = {
  frontend_order_parcel_sizes?: {
    parcels?: ParcelSize[]
  }

  delivery_frontend_order_business_toggle_visibility?: {
    enabled: boolean
  }

  delivery_order_bdui_debounce_duration?: {
    value?: number
  }

  delivery_verticals_enabled_additional_options?: {
    enabled_verticals?: string[]
  }

  form_order_bdui_state_fallback?: InitialRequest

  delivery_frontend_order_promo_banner?: {
    enabled: boolean
  }

  delivery_order_form_bdui_verticals?: {
    enabled_verticals?: string[]
  }

  guts_revealer?: {
    enabled: boolean
  }

  bdui_external_order_switch?: {
    enabled: boolean
  }

  order_creative_block?: CreativeBlockParams
}

export type AddressSuggestResponsePromise = ReturnType<typeof fetchAddressSuggest>

export type AddressSuggestResponse = Awaited<AddressSuggestResponsePromise>

type Seo = {
  title?: string
  description?: string
  heading?: string
}

export type ServerConfig = {
  /**
   * Геолокация пользователя (точка)
   */
  location: Point
  device: Device
  deviceOS?: DeviceOS
  deviceVendor?: DeviceVendor
  trustHost: string
  experiments3?: Experiments
  accountUrl: string
  registrationUrl: string
  routes: RoutesConfig
  version: string
  branding: Brand
  dir: Dir
  basename?: string
  yaAuthProxyHost: string
  passportHost: string
  avatarsHost: string
  passportApiHost: string
  passportAppID?: string
  yandexuid: string
  lang: string
  countryCode: string
  currency: string
  metrikaID?: number | string
  metrikaUID?: number | string
  launch?: LaunchResponse
  utm?: UtmParameters
  phoneMask?: PhoneMask
  mapLang: string
  rules?: PhoneRule[]
  localReplacements?: Record<string, string>
  currencySign: string
  origin: string
  initialProps: InitialProps
  host: string
  tld?: string
  seo?: Seo
  mode: 'b2b_external_order'
  isStaff: boolean
  isYandexTeamUser: boolean
}

type Params = Record<string, string>
type ParamsOrPath = Params | string

export type ClientConfig = {
  getAccountUrl(paramsOrPath?: ParamsOrPath, params?: Params): string
  getRegistrationUrl(params?: Record<string, string>): string
  getPhoneMask(phone?: string): string
  normalizePhone(phone?: string): string
  getPassportUrl(path: string): string
  checkIsValidPhone: (phone?: string) => boolean
}

export type AppConfig = ServerConfig & ClientConfig

export type {MakeUrlApi}

export * from './form'
export * from './outer'
